import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";

import { DELETE_PDF_EVENT } from "../../../utilities/variables/customEventNames";
import {
	getFormActivityData,
	getWorkflowsData,
	isWorkflowActivity,
	saveActivity,
	validateActivity
} from "../../../utilities/activityUtils";
import { deletePrintDocument } from "../../../utilities/rpcHelper";
import { executeWithoutValidation, executeWithSaveValidation } from "../../../utilities/validationUtils";
import { executeBusy, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { PATH_TO_FILE_ATTACHMENT_LOCATION } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { normalizePlatzhalterA12Path } from "../../../utilities/platzhalterHelper";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";
import { StammdatenFormDescriptor } from "../../../utilities/descriptors";

import { handleError } from "./generatePdfSaga";

/**
 * Reagiert auf den Buttonklick zum PDF-Löschen einen Request
 */
export function* deleteGeneratedPdfSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return createEventButtonActionMatcher(DELETE_PDF_EVENT)(action);
	}, handle);
}

function* handle(
	action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonTriggeredPayload>>>
): SagaGenerator<void> {
	const activityId = action.payload.activityId;
	const activity = yield* select(ActivitySelectors.activityById(activityId));

	// Stammdaten haben kein Prozess-/Screen-Konzept, gegen das eine vollumfängliche
	// Validierung sinnvoll ausgeführt werden könnte – dort wird ungeprüft gelöscht.
	const stammdatenForm = StammdatenFormDescriptor.isInstance(activity?.descriptor);

	yield* call(executeBusy, function* (): SagaGenerator<void> {
		yield* call(executeWithoutValidation, activityId, deleteDocument(action, stammdatenForm));
	});
}

function deleteDocument(
	action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonTriggeredPayload>>>,
	stammdatenForm: boolean
): () => SagaGenerator<void> {
	return function* (): SagaGenerator<void> {
		const activityId = action.payload.activityId;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (!activity) {
			throw new Error("Couldn't find activity");
		}

		const data = isWorkflowActivity(activity)
			? yield* call(getWorkflowsData, activity)
			: yield* call(getFormActivityData, activity);
		const group = `generatePdf:${activity.descriptor.instance}`;
		if (!data) {
			yield* call(handleError, "Could not find document data in " + activity.id, group);
			return;
		}

		if (!stammdatenForm) {
			// Speicher-Validierung wie beim regulären Speichern-Button (customSaveSaga): Pflichtfelder müssen
			// dafür nicht zwangsläufig gefüllt sein, das Dokument kann trotzdem gelöscht/gespeichert werden
			// (im Gegensatz zur vollumfänglichen Validierung).
			const isValid = yield* call(
				executeWithSaveValidation<boolean>,
				activity.id,
				function* (): SagaGenerator<boolean> {
					return yield* call(validateActivity, activity.id);
				}
			);
			if (!isValid) {
				return;
			}
		}

		const isSaved = yield* saveActivity(activityId);
		if (!isSaved) {
			yield* call(handleError, "Count not save activity " + activityId, group);
			return;
		}

		// Button annotations holen (enthält Infos wie das zu verwendende Printmodel)
		const annotations = yield* select(
			ModulfUiSelectors.selectButtonAnnotations(activityId, action.payload.engineEvent.payload.buttonPath)
		);

		const pathToFileAttachment = annotations?.find(
			(a: Annotation) => a.name === PATH_TO_FILE_ATTACHMENT_LOCATION
		)?.value;

		try {
			// Request schicken zum löschen Service
			yield* call(deletePrintDocument, data.document.id, normalizePlatzhalterA12Path(pathToFileAttachment));
			if (isWorkflowActivity(activity)) {
				yield* call(reloadTaskAndAntragActivity);
			} else {
				yield* put(ActivityActions.reloadData({ activityId }));
			}
		} catch (e) {
			yield* call(handleError, e, group);
		}
	};
}
